/**
 * Generic NFA drilldown drawer — L1 → L2 → L3 (note) → L4 (approval ladder).
 * The dimension chain lives on the server, so this component works for every
 * analytics widget without any widget-specific code.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronRight, CornerDownRight, Loader2, ExternalLink, Clock, ShieldAlert } from 'lucide-react';
import api from '@/lib/api';
import { useNfaTheme, money, day, stamp } from './nfaTheme';
import { Drawer, Empty, Pill, Btn } from './NfaPrimitives';
import NfaDetailDrawer from './NfaDetailDrawer';

const LEVEL_HINT = {
  NOTES: 'The notes behind this number — open one for its approval ladder',
  LADDER: 'Approval ladder and audit trail for this note',
  L1: 'Top-level breakdown',
  L2: 'Second level — drill again for the individual notes',
  L3: 'Individual notes — open one for its approval ladder',
  L4: 'Approval ladder and audit trail for this note',
};

const Bar = ({ pct, color, T }) => (
  <div className="h-1.5 rounded-full overflow-hidden mt-1" style={{ background: T.surfaceAlt }}>
    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
  </div>
);

export const NfaDrilldownDrawer = ({ widget, view, mode = 'notes', projectId, title,
                                    onClose, initialPath = [] }) => {
  const T = useNfaTheme();
  const [path, setPath] = useState(initialPath);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(true);
  const [openNote, setOpenNote] = useState(null);

  const load = useCallback(() => {
    setBusy(true);
    const qs = new URLSearchParams();
    if (view) qs.set('view', view);
    else { qs.set('widget', widget); qs.set('mode', mode); }
    if (projectId) qs.set('project_id', projectId);
    path.forEach((p) => qs.append('path', p));
    api.get(`/nfa/analytics/drilldown?${qs.toString()}`)
      .then((r) => setD(r.data))
      .catch(() => setD(null))
      .finally(() => setBusy(false));
  }, [widget, view, mode, projectId, path]);
  useEffect(() => { load(); }, [load]);

  const max = Math.max(1, ...(d?.rows || []).map((r) => Math.abs(r.value || r.count || 0)));
  const isLadder = d?.dimension === 'level';
  const isNote = d?.dimension === 'nfa';

  return (
    <>
      <Drawer open onClose={onClose} T={T} width={900} testId="nfa-drilldown-drawer"
        title={d?.title || title || 'Drilldown'}
        subtitle={(
          <div className="mt-1 space-y-1">
            <div className="flex items-center gap-1.5 flex-wrap text-[10.5px]">
              <span className="px-1.5 py-0.5 rounded font-semibold" data-testid="nfa-drill-level"
                style={{ background: T.tint(T.primary), color: T.primary }}>{d?.level || '—'}</span>
              <span style={{ color: T.muted }}>{LEVEL_HINT[d?.level] || ''}</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap text-[10.5px]">
              <button type="button" onClick={() => setPath([])} data-testid="nfa-drill-crumb-root"
                style={{ color: path.length ? T.primary : T.body }}>All</button>
              {(d?.breadcrumb || []).map((b, i) => (
                <span key={`${b.key}-${i}`} className="inline-flex items-center gap-1">
                  <ChevronRight className="w-3 h-3" style={{ color: T.muted }} />
                  <button type="button" data-testid={`nfa-drill-crumb-${i}`}
                    onClick={() => setPath(path.slice(0, i + 1))}
                    style={{ color: i === (d.breadcrumb.length - 1) ? T.ink : T.primary }}>
                    {b.dimensionLabel}: {b.label}
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}>
        {busy && <div className="py-14 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
        {!busy && !d && <Empty T={T}>Could not load this drilldown.</Empty>}

        {!busy && d && isLadder && (
          <div className="space-y-3" data-testid="nfa-drill-ladder">
            <div className="rounded-lg p-3" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-mono text-[10.5px]" style={{ color: T.primary }}>{d.note.nfa_no}</div>
                  <div className="text-[13px] font-semibold" style={{ color: T.ink }}>{d.note.title}</div>
                  <div className="text-[10.5px] mt-0.5" style={{ color: T.muted }}>
                    {d.note.nfa_type} · {d.note.vendor} · WBS {d.note.wbsLabel} · raised by {d.note.raiser}
                    {' '}on {day(d.note.raised_on)} · required by {day(d.note.required_by)}
                  </div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[16px] font-semibold tabular-nums" style={{ color: T.ink }}>
                    {money(d.note.value, d.note.currency)}</div>
                  <Pill value={d.note.status} T={T} />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Btn T={T} variant="ghost" testId="nfa-drill-open-note"
                  onClick={() => setOpenNote(d.note.nfa_id)}>
                  <ExternalLink className="w-3.5 h-3.5" /> Open the full note
                </Btn>
                <span className="text-[10px]" style={{ color: T.muted }}>
                  {d.note.attachments} attachment(s) · {d.note.total_levels} levels in route ·
                  {' '}{d.note.cycleDays != null ? `${d.note.cycleDays}d to decide` : 'still running'}
                </span>
              </div>
            </div>

            {d.rows.map((l) => (
              <div key={l.key} className="rounded-lg p-3" data-testid={`nfa-drill-level-${l.level_no}`}
                style={{ background: T.surface,
                         border: `1px solid ${l.overdue ? T.tint(T.red, '66') : T.border}` }}>
                <div className="flex items-center gap-2 flex-wrap text-[11.5px]">
                  <span className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold shrink-0"
                    style={{ background: T.tint(T.primary), color: T.primary }}>{l.level_no}</span>
                  <span className="font-semibold" style={{ color: T.ink }}>{l.approver_role}</span>
                  <Pill value={l.action} T={T} />
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{ background: T.tint(l.sla_outcome === 'MET' ? T.green
                      : l.sla_outcome === 'BREACHED' ? T.red : T.primary),
                      color: l.sla_outcome === 'MET' ? T.green
                        : l.sla_outcome === 'BREACHED' ? T.red : T.primary }}>
                    SLA {l.sla_outcome} · {l.sla_days}d
                  </span>
                  <span className="ml-auto text-[10px] tabular-nums" style={{ color: T.muted }}>
                    {l.parallel_mode === 'ANY' ? 'any one approver' : 'all approvers'} ·
                    {' '}{l.is_mandatory ? 'mandatory' : 'optional'}
                  </span>
                </div>
                <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-2 text-[10.5px]">
                  <div><span style={{ color: T.muted }}>Activated </span>
                    <span style={{ color: T.body }}>{stamp(l.activated_on)}</span></div>
                  <div><span style={{ color: T.muted }}>Acted </span>
                    <span style={{ color: T.body }}>{stamp(l.acted_on)}</span></div>
                  <div><span style={{ color: T.muted }}>Cycle </span>
                    <span style={{ color: T.body }}>{l.cycleDays != null ? `${l.cycleDays}d` : '—'}</span></div>
                  <div><span style={{ color: T.muted }}>Pending </span>
                    <span style={{ color: l.overdue ? T.red : T.body }}>
                      {l.days_pending != null ? `${l.days_pending}d` : '—'}</span></div>
                </div>
                {(l.comment || l.skip_reason) && (
                  <p className="mt-1.5 text-[10.5px] flex items-start gap-1.5" style={{ color: T.body }}>
                    <CornerDownRight className="w-3 h-3 mt-0.5 shrink-0" style={{ color: T.muted }} />
                    {l.comment || l.skip_reason}
                  </p>
                )}
              </div>
            ))}

            <div className="rounded-lg overflow-hidden" data-testid="nfa-drill-events"
              style={{ background: T.surface, border: `1px solid ${T.border}` }}>
              <div className="px-3 py-2 text-[11px] font-semibold"
                style={{ background: T.surfaceAlt, color: T.ink }}>
                Audit trail — {d.events.length} event(s)
              </div>
              {d.events.map((e) => (
                <div key={e.seq} className="px-3 py-1.5 flex items-center gap-2 text-[10.5px]"
                  style={{ borderTop: `1px solid ${T.border}` }}>
                  <span className="tabular-nums w-8 shrink-0" style={{ color: T.muted }}>#{e.seq}</span>
                  <span className="font-semibold" style={{ color: T.ink }}>{e.action}</span>
                  <span style={{ color: T.body }}>{e.actor_role} · {e.actor_id}</span>
                  <span className="ml-auto tabular-nums" style={{ color: T.muted }}>{stamp(e.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!busy && d && isNote && (
          <div className="space-y-2" data-testid="nfa-drill-notes">
            {d.rows.length === 0 && <Empty T={T}>No notes in this slice.</Empty>}
            {d.rows.map((r) => (
              <button type="button" key={r.key} data-testid={`nfa-drill-note-${r.nfa_no}`}
                onClick={() => setPath([...path, r.nfa_id])}
                className="w-full text-left rounded-lg p-3 hover:opacity-90 transition-opacity"
                style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px]" style={{ color: T.primary }}>{r.nfa_no}</div>
                    <div className="text-[12px] font-semibold truncate" style={{ color: T.ink }}>{r.title}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: T.muted }}>
                      {r.nfa_type} · {r.vendor} · WBS {r.wbsLabel} · {r.raiser} · raised {day(r.raised_on)}
                    </div>
                  </div>
                  <div className="ml-auto text-right shrink-0">
                    <div className="text-[13px] font-semibold tabular-nums" style={{ color: T.ink }}>
                      {money(r.value, r.currency)}</div>
                    <div className="flex items-center gap-1.5 justify-end mt-0.5">
                      <Pill value={r.status} T={T} />
                      {r.pending_level && (
                        <span className="text-[9.5px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ background: T.tint(r.overdue ? T.red : T.amber),
                                   color: r.overdue ? T.red : T.amber }}>
                          {r.overdue ? <ShieldAlert className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          L{r.pending_level} {r.pending_role} · {r.days_pending}d
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {!busy && d && !isNote && !isLadder && (
          <div className="space-y-2" data-testid="nfa-drill-groups">
            {d.totals && (
              <div className="flex items-center gap-3 flex-wrap text-[10.5px] px-1" data-testid="nfa-drill-totals">
                <span style={{ color: T.muted }}>{d.dimensionLabel}</span>
                <span className="ml-auto tabular-nums" style={{ color: T.body }}>
                  {d.totals.notes} note(s) · {money(d.totals.value)}
                  {d.totals.overdue ? ` · ${d.totals.overdue} overdue` : ''}
                </span>
              </div>
            )}
            {d.rows.length === 0 && <Empty T={T}>Nothing in this slice.</Empty>}
            {d.rows.map((r) => (
              <button type="button" key={r.key} disabled={!d.nextDimension}
                data-testid={`nfa-drill-group-${r.key}`}
                onClick={() => d.nextDimension && setPath([...path, r.key])}
                className="w-full text-left rounded-lg px-3 py-2.5 hover:opacity-90 transition-opacity disabled:cursor-default"
                style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <div className="flex items-center gap-2 flex-wrap text-[11.5px]">
                  <span className="font-semibold" style={{ color: T.ink }}>{r.label}</span>
                  {!!r.overdue && (
                    <span className="text-[9.5px] px-1.5 py-0.5 rounded-full"
                      style={{ background: T.tint(T.red), color: T.red }}>{r.overdue} overdue</span>
                  )}
                  <span className="ml-auto tabular-nums" style={{ color: T.body }}>
                    {r.notes} note(s) · {r.count} row(s) · {money(r.value)}
                  </span>
                  {d.nextDimension && <ChevronRight className="w-3.5 h-3.5" style={{ color: T.muted }} />}
                </div>
                <Bar T={T} color={r.overdue ? T.red : T.primary}
                  pct={Math.round((Math.abs(r.value || r.count) / max) * 100)} />
                <div className="mt-1 flex items-center gap-3 text-[10px]" style={{ color: T.muted }}>
                  {r.avgCycleDays != null && <span>avg cycle {r.avgCycleDays}d</span>}
                  {r.avgDaysPending != null && <span>avg pending {r.avgDaysPending}d</span>}
                  {r.maxDaysPending != null && <span>oldest {r.maxDaysPending}d</span>}
                  {d.nextDimension && <span className="ml-auto">drill to {d.nextDimensionLabel}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </Drawer>
      {openNote && <NfaDetailDrawer nfaId={openNote} onClose={() => setOpenNote(null)} onChanged={load} />}
    </>
  );
};

export default NfaDrilldownDrawer;
